import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardShell from "@/components/nav/DashboardShell";
import { ArrowRight, Users, ShieldCheck, CheckCircle2, Mail } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then(({data}) => setSummary(data)).catch(() => {});
    api.get("/activity?range=today&limit=6").then(({data}) => setRecent(data)).catch(() => {});
  }, []);

  const greeting = new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening";
  const first = (user?.full_name || "").split(" ")[0];

  return (
    <DashboardShell>
      <div className="mb-10">
        <div className="brand-eyebrow mb-4">TODAY</div>
        <h1 data-testid="dash-greeting" className="brand-headline text-4xl md:text-6xl">
          Good {greeting}, <span className="text-gold">{first}</span>.
        </h1>
        <p className="text-smoke mt-4 text-lg">Here's what your team is handling today.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <StatCard testid="stat-active-employees" icon={<Users size={18} />} label="Active employees" value={summary?.employees?.length ?? "—"} sub="Online now" />
        <StatCard testid="stat-approvals" icon={<ShieldCheck size={18} />} label="Needs approval" value={summary?.pending_approvals ?? "—"} sub="Waiting for you" highlight={summary?.pending_approvals > 0} link="/approvals" />
        <StatCard testid="stat-completed" icon={<CheckCircle2 size={18} />} label="Completed today" value={summary?.completed_today ?? "—"} sub={`${summary?.emails_today ?? 0} emails handled`} />
      </div>

      {/* Employees + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-line">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between">
            <div className="brand-eyebrow">EMPLOYEES</div>
            <Link to="/employees" className="text-smoke text-xs hover:text-white">Manage →</Link>
          </div>
          <div className="divide-y divide-line">
            {(summary?.employees || []).length === 0 && (
              <div className="p-10 text-smoke">No employees yet. <Link to="/pricing" className="text-gold hover:underline">Hire your first →</Link></div>
            )}
            {(summary?.employees || []).map(e => (
              <Link key={e.id} to={`/employees/${e.id}`} data-testid={`dash-emp-${e.ai_employee_slug}`} className="px-6 py-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-4">
                  <StatusDot status={e.status} />
                  <div>
                    <div className="text-white font-semibold">{e.display_name}</div>
                    <div className="text-smoke text-xs">{e.status} · {e.tasks_today || 0} tasks today</div>
                  </div>
                </div>
                <ArrowRight size={16} className="text-smoke" />
              </Link>
            ))}
          </div>
        </div>
        <div className="border border-line">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between">
            <div className="brand-eyebrow">RECENT ACTIVITY</div>
            <Link to="/activity" className="text-smoke text-xs hover:text-white">All →</Link>
          </div>
          <div className="divide-y divide-line">
            {recent.length === 0 && <div className="p-8 text-smoke text-sm">No activity yet today.</div>}
            {recent.map(a => (
              <div key={a.id} className="px-6 py-4">
                <div className="text-white text-sm font-medium">{a.title}</div>
                {a.description && <div className="text-smoke text-xs mt-1 line-clamp-2">{a.description}</div>}
                <div className="text-smoke text-[11px] mt-2">{new Date(a.created_at).toLocaleTimeString([], {hour:'numeric',minute:'2-digit'})}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function StatCard({ label, value, sub, icon, highlight, link, testid }) {
  const Body = (
    <div data-testid={testid} className={`p-6 border transition-colors ${highlight ? "card-white" : "border-line hover:border-gold"}`}>
      <div className="flex items-center justify-between">
        <div className={`brand-eyebrow ${highlight ? "" : ""}`} style={highlight ? {color:'#0B0D10'} : {}}>{label}</div>
        <div className={highlight ? "text-ink" : "text-gold"}>{icon}</div>
      </div>
      <div className={`brand-headline text-5xl mt-4 ${highlight ? "text-ink" : "text-white"}`}>{value}</div>
      <div className={`text-xs mt-2 ${highlight ? "text-ink/60" : "text-smoke"}`}>{sub}</div>
    </div>
  );
  return link ? <Link to={link}>{Body}</Link> : Body;
}

function StatusDot({ status }) {
  const map = { "Working": "dot-green", "Waiting": "dot-amber", "Paused": "dot-gray", "Needs Attention": "dot-red", "Offline": "dot-gray" };
  return <span className={`dot ${map[status] || "dot-gray"}`} />;
}
